import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import {
  FormFieldDateTimePicker,
  FormFieldTextarea,
  FormFieldCheckbox,
} from '@/shared/components/custom/form-field';
import { createManualEntrySchema, type ManualEntryFormValues } from '../schemas';
import { useCreateManualEntry, useUpdateTimeEntry } from '../hooks/useManualEntryMutations';
import type { TimeEntryDto } from '../types';

interface TimeEntryFormDialogProps {
  taskId: string;
  entry?: TimeEntryDto;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function TimeEntryFormDialog({
  taskId,
  entry,
  open,
  onOpenChange,
}: TimeEntryFormDialogProps) {
  const { t } = useTranslation('timeTracking');
  const isEditing = !!entry;

  const createEntry = useCreateManualEntry(taskId);
  const updateEntry = useUpdateTimeEntry(taskId);
  const isPending = createEntry.isPending || updateEntry.isPending;

  const schema = createManualEntrySchema(t);

  const { control, handleSubmit, reset } = useForm<ManualEntryFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { startTime: '', endTime: '', description: '', isBillable: false },
  });

  useEffect(() => {
    if (open) {
      reset({
        startTime: entry?.startTime ?? '',
        endTime: entry?.endTime ?? '',
        description: entry?.description ?? '',
        isBillable: entry?.isBillable ?? false,
      });
    }
  }, [open, entry, reset]);

  async function onSubmit(values: ManualEntryFormValues) {
    try {
      if (isEditing && entry?.id) {
        await updateEntry.mutateAsync({
          id: entry.id,
          payload: {
            startTime: values.startTime,
            endTime: values.endTime,
            description: values.description || null,
            isBillable: values.isBillable,
          },
        });
      } else {
        await createEntry.mutateAsync({
          startTime: values.startTime,
          endTime: values.endTime,
          description: values.description || null,
          isBillable: values.isBillable,
        });
      }
      onOpenChange(false);
    } catch {
      // global handler shows error toast
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="timetracking-entry-form-dialog">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? t('entryForm.title.edit') : t('entryForm.title.create')}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
          <FormFieldDateTimePicker
            control={control}
            name="startTime"
            label={t('entryForm.start')}
            data-testid="timetracking-entry-start"
          />
          <FormFieldDateTimePicker
            control={control}
            name="endTime"
            label={t('entryForm.end')}
            data-testid="timetracking-entry-end"
          />
          <FormFieldTextarea
            control={control}
            name="description"
            label={t('entryForm.description')}
            rows={3}
          />
          <FormFieldCheckbox control={control} name="isBillable" label={t('entryForm.billable')} />

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('entryForm.cancel')}
            </Button>
            <Button type="submit" disabled={isPending} data-testid="timetracking-entry-form-submit">
              {isPending && <Loader2 className="mr-2 size-4 animate-spin" />}
              {t('entryForm.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
